import { FC, ReactNode } from 'react'

import { Button, Cluster, Icon, IconProps, Skeleton, Stack, Trackable, Typography } from '@forto-dev/phoenix-ui-core'
import { Widget, WidgetFooter, WidgetHeader, WidgetTitle } from '@forto-dev/phoenix-ui-core/ship'

type DashboardKpiCardProps = {
  title: string
  value: ReactNode
  subtitle?: string
  icon: IconProps['name']
  variant?: 'primary' | 'neutral' | 'positive' | 'warning' | 'negative'
  testId: string
}

// No link props on purpose: wrap in a router `Link` at the call site so the route typing stays strict.
export const DashboardKpiCard: FC<DashboardKpiCardProps> = ({
  title,
  value,
  subtitle,
  icon,
  variant = 'neutral',
  testId,
}) => (
  <Trackable data-testid={testId}>
    <Widget variant={variant}>
      <WidgetHeader>
        <WidgetTitle>{title}</WidgetTitle>
      </WidgetHeader>
      <WidgetFooter>
        <Cluster gap={4} y="end" x="between" fullWidth>
          <Cluster gap={4} y="start">
            <Icon size="md" name={icon} />
            <Stack gap={2}>
              <Typography variant="h3">{value}</Typography>
              {subtitle ? (
                <Typography variant="small" color="muted">
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>
          </Cluster>
          <Icon size="sm" name="ArrowRight" color="default" />
        </Cluster>
      </WidgetFooter>
    </Widget>
  </Trackable>
)

type DashboardKpiCardErrorProps = {
  title: string
  message: string
  retryLabel: string
  onRetry: () => void
  testId: string
}

export const DashboardKpiCardError: FC<DashboardKpiCardErrorProps> = ({
  title,
  message,
  retryLabel,
  onRetry,
  testId,
}) => (
  <Trackable data-testid={testId}>
    <Widget variant="negative">
      <WidgetHeader>
        <WidgetTitle>{title}</WidgetTitle>
      </WidgetHeader>
      <WidgetFooter>
        <Cluster gap={4} y="center" x="between" fullWidth>
          <Cluster gap={4} y="center">
            <Icon size="md" name="Warning" />
            <Typography variant="small" color="muted">
              {message}
            </Typography>
          </Cluster>
          <Button variant="ghost" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        </Cluster>
      </WidgetFooter>
    </Widget>
  </Trackable>
)

export const DashboardKpiCardSkeleton: FC = () => (
  <Widget variant="neutral">
    <WidgetHeader>
      <WidgetTitle>
        <Skeleton variant="content" />
      </WidgetTitle>
    </WidgetHeader>
    <WidgetFooter>
      <Cluster gap={4} y="start" fullWidth>
        <Skeleton variant="icon" />
        <Stack gap={2} fullWidth>
          <Skeleton variant="content" />
          <Skeleton variant="badge" fullWidth />
        </Stack>
      </Cluster>
    </WidgetFooter>
  </Widget>
)
